import { Slot } from './Slot'
import { Tooltip } from './Tooltip'
import { tv } from '@joyful/utils'
import { isValidElement, useRef, useState } from 'react'
import type { ComponentProps, ReactElement, ReactNode } from 'react'
import { useMergeRefs } from 'react-merge-refs'
import type { VariantProps } from '@joyful/utils'

export const ellipsisVariants = tv({
  base: 'inline-block max-w-full min-w-0',

  defaultVariants: {
    numLines: 1,
    size: 'inherit',
    tone: 'inherit',
    weight: 'normal',
  },

  variants: {
    leading: {
      none: 'leading-none',
      normal: 'leading-normal',
    },

    numLines: {
      1: 'truncate',
      2: 'line-clamp-2 whitespace-normal',
      3: 'line-clamp-3 whitespace-normal',
      4: 'line-clamp-4 whitespace-normal',
      5: 'line-clamp-5 whitespace-normal',
    },

    size: {
      base: 'text-base',
      inherit: {},
      sm: 'text-sm',
      xs: 'text-xs',
    },

    tone: {
      content: 'text-tremor-content',
      emphasis: 'text-tremor-content-emphasis',
      inherit: {},
      strong: 'text-tremor-content-strong',
      subtle: 'text-tremor-content-subtle',
    },

    weight: {
      medium: 'font-medium',
      normal: {},
      semibold: 'font-semibold',
    },

    wrap: {
      break: 'wrap-break-word',
    },
  },
})

export interface EllipsisProps
  extends
    Omit<ComponentProps<'span'>, 'className'>,
    VariantProps<typeof ellipsisVariants> {
  /**
   * @default false
   */
  asChild?: boolean
  children: ReactNode
  onOverflowChange?: (isOverflowing: boolean) => void

  /**
   * @default false
   */
  shouldDisableOverflowTooltip?: boolean
}

export function Ellipsis({
  asChild = false,
  children,
  leading,
  numLines,
  onOverflowChange,
  ref: refProp,
  shouldDisableOverflowTooltip = false,
  size,
  tone,
  weight,
  wrap,
  ...restProps
}: EllipsisProps) {
  const Comp = asChild ? Slot : 'span'

  const ref = useRef<HTMLElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const shouldTrackOverflow =
    !shouldDisableOverflowTooltip || onOverflowChange !== undefined
  const [isOverflowing, setOverflowing] = useState(false)

  const handleRef = (node: HTMLElement | null) => {
    ref.current = node
    resizeObserverRef.current?.disconnect()
    resizeObserverRef.current = null
    if (!node || !shouldTrackOverflow) {
      return
    }

    const checkOverflowing = () => {
      requestAnimationFrame(() => {
        const element = ref.current
        if (!element) {
          return
        }

        const nextIsOverflowing =
          numLines !== undefined && numLines > 1
            ? element.scrollHeight > element.clientHeight
            : element.scrollWidth > element.clientWidth
        setOverflowing(nextIsOverflowing)
        onOverflowChange?.(nextIsOverflowing)
      })
    }

    checkOverflowing()
    const resizeObserver = new ResizeObserver(checkOverflowing)
    resizeObserver.observe(node)
    resizeObserverRef.current = resizeObserver
  }
  const finalRef = useMergeRefs([handleRef, refProp])

  // A parent Slot (e.g. `<Redact asChild>`) passes `className` through
  // restProps. The outer Slot merges it with the variant classes instead of
  // letting the spread replace them.
  const content = (
    <Slot ref={finalRef} {...restProps}>
      <Comp
        className={ellipsisVariants({
          leading,
          numLines,
          size,
          tone,
          weight,
          wrap,
        })}
      >
        {children}
      </Comp>
    </Slot>
  )

  if (isOverflowing && !shouldDisableOverflowTooltip) {
    const ellipsisContent =
      asChild &&
      isValidElement<{
        children: Exclude<EllipsisProps['children'], ReactElement>
      }>(children)
        ? children.props.children
        : children
    return (
      <Tooltip
        contentProps={{
          size: 'md',
        }}
        content={ellipsisContent}
      >
        {content}
      </Tooltip>
    )
  }

  return content
}
